import { type Mood, MoodColourMap, type MoodColourVariant, MoodIconMap, MoodKeys, MoodMap } from "@qendo/trackables";
import { type ReactNode, useMemo, useState } from "react";
import { Pressable, StyleSheet, View, type ViewStyle } from "react-native";
import { FieldLabel } from "~/components/form/FieldLabel";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Items, Margin, Width } from "~/constants/layout";
import { NoName, useErrorBag } from "~/providers/ErrorBagProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { setWidthOnLayout } from "~/util/layout";

export type MoodPickerProps = {
    label?: ReactNode;
    value?: Mood | null;
    changeMood: (mood: Mood) => void;
    name?: string;
    error?: string | null;
    optional?: boolean;
    disabled?: boolean;
};

export const MoodPicker = ({ value, changeMood, error, disabled, label, name = NoName }: MoodPickerProps) => {
    const { colours } = useTheme();
    const errorBag = useErrorBag();
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const buttonSize = useMemo(() => Math.min(60, Math.max(42, width / 5)), [width]);
    const padding = useMemo(() => (buttonSize - 42) / 2, [buttonSize]);
    const field_error = error ?? errorBag.first(name);

    const styles = useMemo(
        () =>
            StyleSheet.create<Record<MoodColourVariant | "neutral" | "button", ViewStyle>>({
                button: {
                    borderRadius: buttonSize,
                    width: buttonSize,
                    height: buttonSize,
                    padding: padding,
                    borderWidth: 1,
                    flexGrow: 0,
                    flexShrink: 0,
                },
                neutral: {
                    backgroundColor: colours.background.primary,
                    borderColor: colours.border.primary,
                },
                danger: {
                    backgroundColor: colours.button["destructive-background"],
                    borderColor: colours.border.danger,
                },
                warning: {
                    backgroundColor: colours.button["warning-background"],
                    borderColor: colours.button.warning,
                },
                success: {
                    backgroundColor: colours.button["success-background"],
                    borderColor: colours.button.success,
                },
            }),
        [colours, buttonSize, padding],
    );

    return (
        <FieldLabel label={label} style={Width.full} onLayout={onLayout} error={field_error}>
            <View style={[Flex.between, Flex.row, Items.center, Width.full, Margin.top_xxs]}>
                {MoodKeys.map((m) => (
                    <Pressable
                        key={MoodIconMap[m]}
                        style={[styles.button, value === m ? styles[MoodColourMap[m]] : styles.neutral]}
                        disabled={disabled}
                        onPress={() => changeMood(m)}
                        accessibilityLabel={MoodMap[m]}
                    >
                        <QendoIcon
                            name={MoodIconMap[m]}
                            size={40}
                            color={value === m ? colours.icon[MoodColourMap[m]] : colours.icon.secondary}
                        />
                    </Pressable>
                ))}
            </View>
        </FieldLabel>
    );
};
